import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateDocusignTemplate,
  AdminDeleteDocusignTemplate,
  AdminFetchDocusignTemplates,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateDocusignTemplatePayload,
  DeleteDocusignTemplatePayload,
  DocusignTemplate,
} from 'src/interfaces/docusignTemplate'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface FetchDocusignTemplatesPayload {
  partnerId?: string
}

export function* onFetchDocusignTemplates(action: AsyncAction<FetchDocusignTemplatesPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchDocusignTemplates.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // The endpoint answers with a raw array rather than `{ data, total }`; the normalizer
    // handles both, so this call site stays correct if it is ever wrapped.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminDocusignPartnerTemplates,
      {
        method: 'GET',
        query: { partnerId },
      },
    )

    const templates = normalizeApiListPayload<DocusignTemplate>(payload)

    yield put(AdminFetchDocusignTemplates.success(templates))

    if (action.next) {
      action.next(null, templates)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.docusignTemplate.onFetchDocusignTemplates] error',
    })

    yield put(AdminFetchDocusignTemplates.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load DocuSign templates'))
    }
  }
}

export function* onCreateDocusignTemplate(action: AsyncAction<CreateDocusignTemplatePayload>) {
  try {
    // The body is built field by field rather than forwarded: the DTO's `docusignId` is what
    // every response calls `docusignTemplateId` (design §2.2), so a spread would risk sending
    // the wrong name and failing validation with a message that names neither field.
    const { payload }: { payload: DocusignTemplate } = yield call(
      callSecureApi,
      Endpoints.AdminDocusignPartnerTemplates,
      {
        method: 'POST',
        body: {
          partnerId: action.payload.partnerId,
          docusignId: action.payload.docusignId,
          title: action.payload.title,
          description: action.payload.description,
        },
      },
    )

    yield put(AdminCreateDocusignTemplate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.docusignTemplate.onCreateDocusignTemplate] error',
    })

    yield put(AdminCreateDocusignTemplate.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create DocuSign template'))
    }
  }
}

export function* onDeleteDocusignTemplate(action: AsyncAction<DeleteDocusignTemplatePayload>) {
  const templateId = action.payload?.templateId

  if (!templateId) {
    yield put(AdminDeleteDocusignTemplate.failed(new Error('Missing templateId')))

    if (action.next) {
      action.next('Missing templateId')
    }

    return
  }

  try {
    // No `forbiddenIsBusinessError` here: unlike the insurance-product delete, this endpoint has
    // no 403 domain rule. A template still referenced by an insurance policy product is rejected
    // by the database foreign key and surfaces as a 500. See the design's §2.5.
    yield call(callSecureApi, Endpoints.AdminDeleteDocusignTemplate, {
      method: 'DELETE',
      params: [{ field: ':templateId', value: templateId }],
    })

    yield put(AdminDeleteDocusignTemplate.success(templateId))

    if (action.next) {
      action.next(null, templateId)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.docusignTemplate.onDeleteDocusignTemplate] error',
    })

    yield put(AdminDeleteDocusignTemplate.failed(error))

    if (action.next) {
      // `resolveErrorMessage` prefers the backend's `payload.message`, so a constraint violation
      // reaches the admin verbatim rather than as a generic failure. Do not replace this with an
      // unconditional string — the fallback only covers an error carrying no message at all.
      action.next(resolveErrorMessage(error, 'Failed to delete DocuSign template'))
    }
  }
}

export function* docusignTemplateSaga() {
  // A refresh or tab switch re-triggers the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchDocusignTemplates.type.REQUEST, onFetchDocusignTemplates)
  // Mutations must each run.
  yield takeEvery(AdminCreateDocusignTemplate.type.REQUEST, onCreateDocusignTemplate)
  yield takeEvery(AdminDeleteDocusignTemplate.type.REQUEST, onDeleteDocusignTemplate)
}
